import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { clientApi } from '../api';
import { fetchClientFlowViaCache } from './useClientFlow';

// Superficie de datos completa de `SubPanelTratos.jsx` -- un formulario por-nodo con 2 modos
// (autenticado vía `op`/`currentUserId`, público vía `publicUuid`) que hoy carga sus datos en
// una única función async con varios `await` secuenciales y ramas condicionales, no en queries
// declarativas independientes. Convertir eso a `useQuery`/dependent queries de verdad es un
// cambio de forma más grande de lo que le corresponde al paso `a` (que es "sacar `clientApi.*`
// crudo de los componentes", no "rediseñar el modelo de datos del formulario más complejo del
// slice") -- decisión explícita, no un olvido: ver 02.3-client.md §4 y la entrada de bitácora de
// este paso.

export function clientDocumentsQueryKey(uid) {
    return ['client', 'documents', uid];
}

async function fetchDocsByUserQueryFn(uid) {
    const d = await clientApi.fetchDocsByUser(uid);
    return d.docs || [];
}

// `OpDetailPanel.jsx` (`opDocs`, mapa por `document_type`) y `SubPanelTratos.jsx` (`allDocs`,
// lista completa para el BFS de "datos recibidos") piden el mismo endpoint para el mismo `uid`
// -- la misma duplicación que `fetchFlow` (ver useClientFlow.js). Hook declarativo para el call
// site simple de `OpDetailPanel`.
export function useClientDocumentsByUser(uid) {
    return useQuery({
        queryKey: clientDocumentsQueryKey(uid),
        queryFn: () => fetchDocsByUserQueryFn(uid),
        enabled: !!uid,
    });
}

// `SubPanelTratos.jsx` pide esta misma lista desde dentro de su efecto orquestador (no puede
// llamar al hook de arriba condicionalmente) -- vía la cache compartida, mismo query key, para
// no duplicar el fetch cuando ambos componentes están montados sobre el mismo trato.
export function fetchClientDocumentsByUserViaCache(queryClient, uid) {
    return queryClient.fetchQuery({ queryKey: clientDocumentsQueryKey(uid), queryFn: () => fetchDocsByUserQueryFn(uid) });
}

// Fase 5.15 -- `SubPanelTratos.jsx` (presentacional) leía la caché de React Query vía
// `useQueryClient()` directo para orquestar su carga de datos. Este hook encapsula ese acceso:
// el componente recibe 2 lectores ya ligados al `QueryClient` y no importa `@tanstack/react-query`.
export function useClientCacheReaders() {
    const queryClient = useQueryClient();
    const readDocumentsByUser = useCallback(
        (uid) => fetchClientDocumentsByUserViaCache(queryClient, uid),
        [queryClient],
    );
    const readFlow = useCallback((uid) => fetchClientFlowViaCache(queryClient, uid), [queryClient]);
    return { readDocumentsByUser, readFlow };
}

// El resto -- doc individual por tipo, doc público, guardar, visibilidad -- se queda como
// wrappers simples 1:1 de `clientApi`, llamados desde el mismo efecto orquestador/autosave que
// ya existían en `SubPanelTratos.jsx` (ver nota grande arriba sobre por qué no son
// `useQuery`/`useMutation` en este paso).
export function fetchClientDocByType(uid, documentType) {
    return clientApi.fetchDocByType(uid, documentType);
}

export function fetchClientPublicDoc(publicUuid) {
    return clientApi.fetchPublicDoc(publicUuid);
}

export function saveClientPublicDoc(publicUuid, payload) {
    return clientApi.savePublicDoc(publicUuid, payload);
}

export function saveClientDoc(payload) {
    return clientApi.saveDoc(payload);
}

export function setClientDocumentVisibility(docId, index, access) {
    return clientApi.setDocumentVisibility(docId, index, access);
}
